/**
 * Configuration Step (Step 3)
 * Final configuration and review
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, Building2, User } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import type { Step1Data, Step2Data, Step3Data, ParentNetwork } from '../../../types/networks.types';
import { apiClient } from '../../../api/apiClient';

interface ConfigurationProps {
  platformData: Step1Data;
  credentialsData: Step2Data;
  data: Step3Data;
  onChange: (data: Step3Data) => void;
}

export const Configuration: React.FC<ConfigurationProps> = ({
  platformData,
  credentialsData,
  data,
  onChange
}) => {
  const [parentNetworks, setParentNetworks] = useState<ParentNetwork[]>([]);
  const [isLoadingNetworks, setIsLoadingNetworks] = useState(false);

  // Fetch parent networks on mount
  useEffect(() => {
    const fetchParentNetworks = async () => {
      setIsLoadingNetworks(true);
      try {
        // apiClient.get() already unwraps the data, so response is ParentNetwork[]
        // Note: apiClient already has '/api' as base URL, so don't include it here
        const parentNetworksData = await apiClient.get<ParentNetwork[]>(
          '/networks/parent-networks'
        );
        setParentNetworks(parentNetworksData || []);
      } catch (error) {
        console.error('Failed to fetch parent networks:', error);
        // Non-critical error, continue without parent networks
        setParentNetworks([]);
      } finally {
        setIsLoadingNetworks(false);
      }
    };

    fetchParentNetworks();
  }, []);

  return (
    <div className="space-y-6">
      {/* Review Section */}
      <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-1">
              Connection Verified
            </h3>
            <p className="text-sm text-green-800 dark:text-green-400">
              Your credentials have been tested successfully. Configure the final settings below.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Review */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Platform Information
        </h3>
        <div className="flex items-center gap-4">
          {platformData.platform_logo && (
            <img
              src={platformData.platform_logo}
              alt={platformData.platform_name}
              className="h-10 object-contain"
            />
          )}
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {platformData.platform_name}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {credentialsData.login_url}
            </p>
          </div>
        </div>
      </div>

      {/* Account Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Account Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.account_name}
          onChange={(e) => onChange({ ...data, account_name: e.target.value })}
          placeholder="e.g., My NetRefer Account"
          className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          required
        />
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          A friendly name to identify this connection
        </p>
      </div>

      {/* Account Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Account Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {/* Direct Option */}
          <button
            type="button"
            onClick={() => onChange({ ...data, account_type: 'direct', parent_network_id: null })}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 ${
              data.account_type === 'direct'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <User className={`w-5 h-5 ${
              data.account_type === 'direct' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
            }`} />
            <div className="text-left">
              <div className={`font-medium ${
                data.account_type === 'direct'
                  ? 'text-blue-900 dark:text-blue-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                Direct
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Your own account
              </div>
            </div>
          </button>

          {/* Network Option */}
          <button
            type="button"
            onClick={() => onChange({ ...data, account_type: 'network' })}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 ${
              data.account_type === 'network'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <Building2 className={`w-5 h-5 ${
              data.account_type === 'network' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
            }`} />
            <div className="text-left">
              <div className={`font-medium ${
                data.account_type === 'network'
                  ? 'text-blue-900 dark:text-blue-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                Network
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Through a network
              </div>
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
          {data.account_type === 'direct'
            ? 'You work directly with this affiliate program'
            : 'You work through an affiliate network (e.g., Raketech, Kiickr, KeyAffiliates)'}
        </p>
      </div>

      {/* Parent Network Selection (only if account_type === 'network') */}
      {data.account_type === 'network' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Parent Network <span className="text-red-500">*</span>
          </label>
          <Select.Root
            value={data.parent_network_id || ''}
            onValueChange={(value) => onChange({ ...data, parent_network_id: value })}
            disabled={isLoadingNetworks}
          >
            <Select.Trigger className="w-full inline-flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <Select.Value placeholder={isLoadingNetworks ? 'Loading networks...' : 'Select network...'} />
              <ChevronDown className="w-4 h-4" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                <Select.Viewport className="p-1">
                  {parentNetworks.length === 0 && !isLoadingNetworks && (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No networks available
                    </div>
                  )}
                  {parentNetworks.map((network) => (
                    <SelectItem key={network.id} value={network.id}>
                      {network.name}
                    </SelectItem>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Select the affiliate network you work through (e.g., Raketech, Kiickr)
          </p>
        </div>
      )}

      {/* Sync Frequency */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Sync Frequency
        </label>
        <Select.Root 
          value={data.sync_frequency.toString()} 
          onValueChange={(value) => onChange({ ...data, sync_frequency: parseInt(value) })}
        >
          <Select.Trigger className="w-full inline-flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200">
            <Select.Value />
            <ChevronDown className="w-4 h-4" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <Select.Viewport className="p-1">
                <SelectItem value="15">Every 15 minutes</SelectItem>
                <SelectItem value="30">Every 30 minutes</SelectItem>
                <SelectItem value="60">Every hour</SelectItem>
                <SelectItem value="120">Every 2 hours</SelectItem>
                <SelectItem value="0">Manual only</SelectItem>
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          How often to automatically sync data from this network
        </p>
      </div>

      {/* Commission Model */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Commission Model
        </label>
        <Select.Root 
          value={data.commission_model} 
          onValueChange={(value) => onChange({ ...data, commission_model: value as any })}
        >
          <Select.Trigger className="w-full inline-flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200">
            <Select.Value />
            <ChevronDown className="w-4 h-4" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <Select.Viewport className="p-1">
                <SelectItem value="revshare">Revenue Share</SelectItem>
                <SelectItem value="cpa">Cost Per Acquisition (CPA)</SelectItem>
                <SelectItem value="hybrid">Hybrid (RevShare + CPA)</SelectItem>
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          The commission structure for this network
        </p>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notes (Optional)
        </label>
        <textarea
          value={data.notes}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Add any additional notes or information..."
          rows={4}
          className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
        />
      </div>

      {/* Configuration Summary */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3">
          Configuration Summary
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-600 dark:text-gray-400">Platform:</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">{platformData.platform_name}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600 dark:text-gray-400">Account Name:</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {data.account_name || 'Not set'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600 dark:text-gray-400">Account Type:</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {data.account_type === 'direct' ? 'Direct' : 'Network'}
            </dd>
          </div>
          {data.account_type === 'network' && data.parent_network_id && (
            <div className="flex justify-between">
              <dt className="text-gray-600 dark:text-gray-400">Parent Network:</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {parentNetworks.find(n => n.id === data.parent_network_id)?.name || 'Not set'}
              </dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-gray-600 dark:text-gray-400">Sync Frequency:</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {data.sync_frequency === 0 ? 'Manual only' : 
               data.sync_frequency === 15 ? 'Every 15 minutes' :
               data.sync_frequency === 30 ? 'Every 30 minutes' :
               data.sync_frequency === 60 ? 'Every hour' :
               'Every 2 hours'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600 dark:text-gray-400">Commission Model:</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {data.commission_model === 'revshare' ? 'Revenue Share' :
               data.commission_model === 'cpa' ? 'CPA' : 'Hybrid'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

// Select Item Component
interface SelectItemProps {
  value: string;
  children: React.ReactNode;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <Select.Item
        className="relative flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 outline-none select-none data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-700"
        {...props}
        ref={forwardedRef}
      >
        <Select.ItemText>{children}</Select.ItemText>
      </Select.Item>
    );
  }
);

SelectItem.displayName = 'SelectItem';
